import { useCallback, useContext, useMemo } from 'react';
import { getExactKeyPath } from '../filterQueryFormat/logic/LogicGroup';
import MissingField from '../filterQueryFormat/MissingField';
import { isOperator, swapOperator } from '../filterQueryFormat/operator/Operator';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { useFieldConditionChange } from './useFieldConditionChange';
import { useGetOperators } from './useGetOperators';
import { useStrategy } from './useStrategy';
export const usePropertyChangedNameAndValueFilter = (filterFamily, condition, conditionPath) => {
  const {
    internalDraftValue,
    fetchMore
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getOperators
  } = useGetOperators();
  const {
    getField
  } = useStrategy();
  const hydratedCondition = useMemo(() => {
    let hydrated = condition;
    if (!hydrated.referencedObjectTypeId) {
      const eventParentLogicGroup = getExactKeyPath(conditionPath.pop().pop(), internalDraftValue);
      hydrated = hydrated.set('referencedObjectTypeId', eventParentLogicGroup.filterFamily);
    }
    const isPropertyValueMissing = hydrated.propertyName && !hydrated.propertyValue;
    const isPropertyValueFieldMissing = hydrated.propertyValue && isOperator(hydrated.propertyValue) && MissingField.isMissingField(hydrated.propertyValue.field);
    if (isPropertyValueMissing || isPropertyValueFieldMissing) {
      const referencedPropertyField = getField(hydrated.referencedObjectTypeId, hydrated.propertyName) || MissingField({
        name: hydrated.propertyName
      });
      const operators = getOperators(hydrated.referencedObjectTypeId, referencedPropertyField);
      if (!operators || operators.size < 1) {
        throw new Error(`No operators found for ${referencedPropertyField.name} property`);
      }
      hydrated = hydrated.set('propertyValue',
      //@ts-expect-error operator types need improvements
      operators.first().of(referencedPropertyField));
    }
    return hydrated;
  }, [condition, conditionPath, internalDraftValue, getField, getOperators]);
  const {
    onFieldConditionValueChange
  } = useFieldConditionChange(filterFamily, conditionPath, hydratedCondition);
  const onPropertyNameChange = useCallback(evt => {
    const newCondition = evt.target.value;
    fetchMore({
      variables: {
        assetFamily: hydratedCondition.referencedObjectTypeId,
        ids: [newCondition.propertyName]
      }
    }).then(() => {
      const selectedField = getField(hydratedCondition.referencedObjectTypeId, newCondition.propertyName);
      if (!selectedField) {
        throw new Error(`Field not found for ${newCondition.propertyName} property`);
      }
      const operators = getOperators(hydratedCondition.referencedObjectTypeId, selectedField);
      if (!operators || operators.size < 1) {
        throw new Error(`No operators found for ${selectedField.name} property`);
      }

      //@ts-expect-error types need improvements
      const valueCondition = operators.first().of(selectedField);
      onFieldConditionValueChange(newCondition.set('propertyValue', valueCondition), false);
    });
  }, [fetchMore, getField, getOperators, onFieldConditionValueChange, hydratedCondition.referencedObjectTypeId]);
  const onPropertyValueOperatorChange = useCallback(newOperator => {
    const newPropertyValueCondition = swapOperator(newOperator, hydratedCondition.propertyValue);
    onFieldConditionValueChange(hydratedCondition.set('propertyValue', newPropertyValueCondition), false);
  }, [hydratedCondition, onFieldConditionValueChange]);
  const onPropertyValueValueChange = useCallback(newPropertyValueCondition => {
    onFieldConditionValueChange(hydratedCondition.set('propertyValue', newPropertyValueCondition), true);
  }, [hydratedCondition, onFieldConditionValueChange]);
  return {
    hydratedCondition,
    onPropertyNameChange,
    onPropertyValueOperatorChange,
    onPropertyValueValueChange
  };
};